import React from "react";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { TokenBalance } from "../api/usage";
import { useAppSelector } from "../store/hooks";
import {
  APP_ACCENT_GOLD,
  APP_BORDER,
  APP_TEXT_SECONDARY,
} from "./createShared";
import { cardPillSizingSx } from "./common/buttonSizing";
import { subscriptionDetail, billingDate } from "./billingModel";
import { UsageSkeleton } from "./common/UsageLoadingShell";
import { FableInfoTooltip, FORM_FIELD_RHYTHM } from "./common/StoryFormControls";
import { productCardTitleSx, productSectionTitleSx } from "./common/productTypography";

const cardSx = {
  p: { xs: 2, sm: 2.5 },
  border: `1px solid ${APP_BORDER}`,
  borderRadius: "16px",
  bgcolor: "#1B1E28",
  minWidth: 0,
};
const actionSx = {
  ...cardPillSizingSx,
  flexShrink: 0,
  color: "#fff",
  border: `1px solid ${APP_BORDER}`,
  borderRadius: 999,
  textTransform: "none",
  alignSelf: "start",
};
const count = (value: number) => value.toLocaleString();

export default function BillingOverview({
  wallet,
  loading,
}: {
  wallet?: TokenBalance;
  loading?: boolean;
}) {
  const legacyPro = useAppSelector((state) => state.app.is_platinum);
  const legacyPremium = useAppSelector((state) => state.app.is_premium);
  const legacyTrial = useAppSelector((state) => state.app.is_trial);
  const trialEnd = useAppSelector((state) => state.app.trial_end_date);
  const membership = wallet?.available ? wallet.membership : undefined;
  const isPro = membership ? membership.tier === "pro" : legacyPro;
  const isPremium = membership ? membership.tier === "premium" : legacyPremium;
  const isTrial = wallet?.trial?.active ?? legacyTrial;
  const paid = isPro || isPremium;
  const available = wallet?.available === true;
  const allowance = wallet?.monthlyAllowance || 0;
  const nextAllocation = billingDate(wallet?.nextSubscriptionAllocationAt);
  const expires = billingDate(wallet?.subscriptionExpiresAt);
  const subscription =
    available && typeof wallet.subscription === "number" && Number.isFinite(wallet.subscription)
      ? count(wallet.subscription)
      : null;
  const purchased =
    available && typeof wallet.purchased === "number"
      ? count(wallet.purchased)
      : null;
  const hasAllowance = available && Number.isFinite(allowance) && allowance > 0;
  const remainingPercent = hasAllowance && subscription !== null
    ? Math.min(100, Math.max(0, ((wallet?.subscription || 0) / allowance) * 100))
    : null;
  const allocationStatus = nextAllocation
    ? `Refills ${nextAllocation}`
    : expires
      ? `Use by ${expires}`
      : !available || subscription === null
        ? "Allocation details unavailable"
        : hasAllowance
          ? "Refill date unavailable"
          : (wallet?.subscription || 0) > 0
            ? "Allowance unavailable"
            : "No active allocation";
  return (
    <Stack spacing={FORM_FIELD_RHYTHM.section} aria-busy={!!loading}
      role={loading ? "status" : undefined} aria-label={loading ? "Loading token balances" : undefined}>
      <Box component="section" aria-labelledby="billing-plan-title">
        <Typography
          id="billing-plan-title"
          component="h2"
          sx={productSectionTitleSx}
          mb={1.25}
        >
          Plan
        </Typography>
        <Stack
          sx={{ ...cardSx, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 1.5, alignItems: 'start' }}
        >
            <Typography sx={productCardTitleSx}>
              {loading ? <UsageSkeleton component="span" width={130} height="1.35em" sx={{ display: "inline-block", verticalAlign: "middle" }} />
                : isPro ? "Fable Pro" : isPremium ? "Fable Premium" : "Fable Free"}
            </Typography>
          {loading ? <UsageSkeleton width={100} height={34} sx={{ borderRadius: 999 }} /> : <Button component={Link} to="/manage-subscription" sx={actionSx}>
            {paid ? "Manage subscription" : "View plans"}
          </Button>}
            <Typography color={APP_TEXT_SECONDARY} mt={FORM_FIELD_RHYTHM.mini} sx={{ gridColumn: '1 / -1' }}>
              {loading ? <UsageSkeleton component="span" width={280} height={18} sx={{ display: "inline-block", verticalAlign: "middle" }} />
                : subscriptionDetail(wallet, { paid, isTrial, trialEnd })}
            </Typography>
            {!loading && wallet?.trial?.used && !isTrial && (
              <Typography color={APP_TEXT_SECONDARY} fontSize={13} mt={1} sx={{ gridColumn: '1 / -1' }}>
                Trial used
              </Typography>
            )}
        </Stack>
      </Box>
      <Box component="section" aria-labelledby="billing-subscription-title">
        <Stack direction="row" alignItems="center" gap={FORM_FIELD_RHYTHM.inlineControl} mb={1.25}>
          <Typography id="billing-subscription-title" component="h2" sx={productSectionTitleSx}>
            Subscription tokens
          </Typography>
          <FableInfoTooltip buttonLabel="About subscription tokens"
            title="Subscription tokens are used first. Unused tokens expire at the end of each allocation period. Monthly refills follow your subscription date." />
        </Stack>
        <Stack gap={FORM_FIELD_RHYTHM.inlineControl} sx={cardSx}>
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="baseline"
            gap={1}
          >
            <Typography fontSize={18} fontWeight={600}>
              {isTrial ? "Trial allowance" : "Monthly allowance"}
            </Typography>
            <Typography fontSize={16} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {loading ? <UsageSkeleton component="span" width={150} height={24} sx={{ display: "inline-block", verticalAlign: "middle" }} />
                : subscription === null ? "Balance unavailable"
                  : hasAllowance ? `${subscription} / ${count(allowance)} tokens`
                    : `${subscription} tokens remaining`}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={FORM_FIELD_RHYTHM.inlineControl}>
            <Typography color={APP_TEXT_SECONDARY}>
              {loading ? <UsageSkeleton component="span" width={180} height={18} sx={{ display: "inline-block", verticalAlign: "middle" }} /> : allocationStatus}
            </Typography>
            {loading ? <UsageSkeleton width={65} height={18} /> : remainingPercent !== null && (
              <Typography color={APP_TEXT_SECONDARY} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {remainingPercent.toLocaleString(undefined, { maximumFractionDigits: 1 })}% left
              </Typography>
            )}
          </Stack>
          {loading ? <UsageSkeleton height={8} sx={{ borderRadius: 999 }} /> : remainingPercent !== null ? (
            <LinearProgress
              variant="determinate"
              aria-label="Subscription tokens remaining"
              aria-valuetext={`${subscription} of ${count(allowance)} tokens remaining`}
              value={remainingPercent}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: APP_BORDER,
                "& .MuiLinearProgress-bar": {
                  bgcolor: APP_ACCENT_GOLD,
                  borderRadius: 999,
                },
              }}
            />
          ) : <Box aria-hidden="true" sx={{ height: 8, borderRadius: 999, bgcolor: APP_BORDER }} />}
        </Stack>
      </Box>
      <Box component="section" aria-labelledby="billing-purchased-title">
        <Stack direction="row" alignItems="center" gap={FORM_FIELD_RHYTHM.inlineControl} mb={1.25}>
          <Typography id="billing-purchased-title" component="h2" sx={productSectionTitleSx}>
            Top-up tokens
          </Typography>
          <FableInfoTooltip buttonLabel="About top-up tokens"
            title="Top-up tokens never expire and are used after your subscription tokens. This balance includes any existing credits and rewards." />
        </Stack>
        <Stack
          sx={{ ...cardSx, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 1.5, alignItems: 'start' }}
        >
            <Typography fontSize={18} fontWeight={600} lineHeight={1.35} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {loading ? <UsageSkeleton component="span" width={220} height={24} sx={{ display: "inline-block", verticalAlign: "middle" }} /> : purchased === null
                ? "Balance unavailable"
                : `${purchased} tokens remaining`}
            </Typography>
          {loading ? <UsageSkeleton width={80} height={34} sx={{ borderRadius: 999 }} />
            : <Button component={Link} to="/buy-tokens" aria-label="Add more top-up tokens" sx={actionSx}>
              Add more
            </Button>}
            <Typography color={APP_TEXT_SECONDARY} mt={FORM_FIELD_RHYTHM.mini} sx={{ gridColumn: '1 / -1' }}>
              Current balance
            </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}
